import { useCallback, useEffect, useState } from "react";
import { useBusyBlockerContext } from "../contexts/busy-blocker-context";
import { useDashboardContext } from "../contexts/dashboard-context";
import { useMessage } from "./useMessage";
import { useReuseZonesForTemplates } from "./useReuseZonesForTemplates";
import { useLogicalSelectedPage, useSelectedSpread } from "./useSelection";
import { usePageTemplates } from "./useTemplates";

function getTemplateCode(page: eXpress.core.IPageZone | undefined | null) {
	return page?.name.toLowerCase();
}

/**
 * Use this hook to get the current template (name) or change the template of provided page
 */
export function useTemplate() {
	const page = useLogicalSelectedPage();
	const spread = useSelectedSpread();
	const dashboard = useDashboardContext();

	// Remark:
	// Should we enable/disable button if there are no templates for the current page?
	// 1) disable button
	//   - we must check here if there are templates for the current spread kind
	//   - templates submenu should be empty so it looks closed
	// 2) always enable button
	//   - no need to get/check templates for page
	//   - the submenu should not appear closed (be empty), a label like 'no templates available for this page' needed?
	// I did choose option 1) (similar as backgrounds, not like DC)

	// Disable when page doesn't allow templates
	const { value: pageTemplates } = usePageTemplates();
	const { busyBlocker } = useBusyBlockerContext();

	const [templateCode, setTemplateCode] = useState(() => getTemplateCode(page));
	const [templateCodeLeftPage, setTemplateCodeLeftPage] = useState(() =>
		getTemplateCode(spread?.leftPage),
	);
	const [templateCodeRightPage, setTemplateCodeRightPage] = useState(() =>
		getTemplateCode(spread?.rightPage),
	);
	const [getAndSetReuseZones] = useReuseZonesForTemplates();

	// Update template code after a template is applied
	useMessage(
		useCallback(
			(message) => {
				if (eXpress.core.canChangeUsedTemplates(message)) {
					setTemplateCode(getTemplateCode(page));
					setTemplateCodeLeftPage(getTemplateCode(spread?.leftPage));
					setTemplateCodeRightPage(getTemplateCode(spread?.rightPage));
				}
			},
			[page, spread?.leftPage, spread?.rightPage],
		),
	);

	// Update template code if one of the pages changes
	useEffect(() => {
		setTemplateCode(getTemplateCode(page));
		setTemplateCodeLeftPage(getTemplateCode(spread?.leftPage));
		setTemplateCodeRightPage(getTemplateCode(spread?.rightPage));
	}, [page, spread?.leftPage, spread?.rightPage]);

	const applyTemplate = (
		newTemplateCode: eXpress.LowerCaseString,
		absoluteTemplateUrl: string,
		targetPage: eXpress.core.IPageZone | undefined,
	) => {
		if (!canApplyTemplate(targetPage)) return Promise.resolve();

		const zonesToReuseForSpread = getAndSetReuseZones(targetPage);

		return busyBlocker.while(
			dashboard.addTemplateToPageAsync(
				newTemplateCode,
				absoluteTemplateUrl,
				targetPage,
				zonesToReuseForSpread,
			),
		);
	};

	const canApplyTemplate = (targetPage: eXpress.core.IPageZone | undefined) => {
		const canChangeTemplateOnPage = dashboard.canAddTemplateToPage(targetPage);
		const areTemplatesAvailableForPage = pageTemplates.length > 0;

		return canChangeTemplateOnPage && areTemplatesAvailableForPage;
	};

	return [
		/** The Template code used on this page */
		templateCode,
		templateCodeLeftPage,
		templateCodeRightPage,

		/** If a page can accept a template, a function to apply a template exists*/
		canApplyTemplate,
		applyTemplate,
	] as const;
}
